import { useEffect, useState } from "react";
import { ProductCarousel } from "./ProductCarousel";
import { useRouter } from "next/router";
import type { AlgoliaItem } from "@lib/analytics/analyticsTypes";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import hexRgb from "hex-rgb";
import type { IPagePath } from "@contentful-api/types/contentful";
import type { Document } from "@contentful/rich-text-types";
import { NextLink } from "@ui/components/navigation/link/NextLink";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { DEFAULT_ATTRIBUTES_TO_RETRIEVE } from "@lib/algolia/constants";
import { AlgoliaProductProperties } from "@lib/wrappers/algolia";

interface ISearchProductsHitsToCarouselProps {
    products?: string[];
    headline?: string;
    assistiveMessage?: string;
    sortingOrder?: string[];
    carouselType?: string;
    authorizationInvitation?: Document;
    extraLink?: IPagePath;
    extraLinkLabel?: string;
    id?: string;
}

export const SearchProductsHitsToCarousel = (props: ISearchProductsHitsToCarouselProps) => {
    const {
        products,
        headline,
        assistiveMessage,
        sortingOrder,
        carouselType,
        authorizationInvitation,
        extraLink = null,
        extraLinkLabel = null,
        id,
    } = props;

    const { locale } = useRouter();
    const [hits, setHits] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const { browseClient } = useAlgoliaClient(locale);

    const filters = products?.map((id) => `objectID:${id}`).join(" OR ") ?? "";

    useEffect(() => {
        if (filters) {
            setIsLoading(true);
            browseClient
                .browseObjects({
                    filters: filters,
                    facetFilters: ["hasStock:true"],
                    attributesToRetrieve: [
                        ...DEFAULT_ATTRIBUTES_TO_RETRIEVE,
                        AlgoliaProductProperties.descriptiveHeading,
                    ],
                    batch: (batch) => {
                        const normalizedHits = batch.map((hit, index) => {
                            return {
                                ...hit,
                                id: hit.id ?? hit.objectID,
                                productColors: Array.isArray(hit.productColors)
                                    ? hit.productColors?.map((it) => ({
                                          priceWithDiscount: it.priceRaw,
                                          priceWithoutDiscount: it.listPriceRaw,
                                          image: it.image,
                                          objectID: it.objectID,
                                          colorCode: it.colorCode,
                                          imageBackgroundColor: it.imageBackgroundColor,
                                          discountPercentage: it.discountPercentage,
                                          hasDiscount: it.hasDiscount,
                                          hasStock: it.hasStock,
                                          lowestPrice: it.lowestPriceRaw,
                                          discountPercentageLowestPrice:
                                              it.discountPercentageLowestPrice,
                                      }))
                                    : hit.productColors,
                                queryID: hit.__queryID,
                                index,
                                productName: hit.name,
                                priceWithoutDiscount: hit.listPriceRaw,
                                priceWithDiscount: hit.priceRaw,
                                lowestPrice: hit.lowestPriceRaw,
                                discountPercentageLowestPrice: hit.discountPercentageLowestPrice,
                                backgroundColor: hexRgb("FFF"),
                                imageBackgroundColor: hexRgb(
                                    (hit.backgroundColor ?? "E9E9E9") as string,
                                    {
                                        format: "css",
                                        alpha: 1,
                                    }
                                ),
                            };
                        });

                        if (sortingOrder && sortingOrder?.length) {
                            normalizedHits?.sort?.((a, b) => {
                                return (
                                    sortingOrder?.findIndex((it) => it === a.objectID) -
                                    sortingOrder?.findIndex((it) => it === b.objectID)
                                );
                            });
                        }

                        setHits(normalizedHits);
                    },
                })
                .then(() => setIsLoading(false))
                .finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
        }
    }, [filters, browseClient, sortingOrder]);

    useEffect(() => {
        const analyticsTimeout = setTimeout(
            () =>
                AnalyticsClient.loadItemsList(hits as unknown as AlgoliaItem[], {
                    listName: carouselType,
                }),
            3000
        );
        return () => {
            clearTimeout(analyticsTimeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hits]);

    const ExtraLinkComponent = () => {
        const { completeSlug, label } = extraLink?.fields ?? {};
        if (completeSlug) {
            return <NextLink href={completeSlug}>{extraLinkLabel ?? label}</NextLink>;
        }
        return null;
    };

    //TODO: INTL assistiveMessage
    return (
        <ProductCarousel
            slideData={hits}
            isLoading={isLoading}
            headline={headline}
            assistiveMessage={assistiveMessage}
            carouselType={carouselType}
            authorizationInvitation={authorizationInvitation}
            extraLink={<ExtraLinkComponent />}
            id={id}
        />
    );
};
